import { Link } from "react-router-dom";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Users,
  Award,
  Clock,
  Heart,
  Shield,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";

export default function PainMedicinePage() {
  const { open: openAppointment } = useAppointment();
  const doctors = [];


  const subspecialties = [
    {
      title: "Chronic Pain Management",
      description:
        "Back pain, neck pain, joint pain, nerve pain & fibromyalgia.",
      image: "/ChronicPainManagement.jpg",
    },
    {
      title: "Interventional Pain Medicine",
      description: "Image-guided injections & minimally invasive procedures.",
      image: "/InterventionalPainMedicine.jpg",
    },
    {
      title: "Spine Pain Management",
      description:
        "Herniated discs, sciatica, spondylosis & spinal stenosis.",
      image: "/SpinePainManagement.jpg",
    },
    {
      title: "Cancer Pain Management",
      description: "Advanced therapies for cancer-related pain.",
      image: "/CancerPainManagement.jpg",
    },
    {
      title: "Neuropathic Pain Care",
      description: "Nerve damage, diabetic neuropathy & neuralgias.",
      image: "/NeuropathicPainCare.jpg",
    },
    {
      title: "Acute Pain Services (APS)",
      description:
        "Post-surgical pain and emergency pain management.",
      image: "/AcutePainServices(APS).jpg",
    },
  ];

  const treatments = [
    "Epidural Injections (Cervical, Thoracic & Lumbar)",
    "Facet Joint Injections & Nerve Blocks",
    "Radiofrequency Ablation (RFA) for Chronic Pain",
    "Trigger Point Injections",
    "Sciatica & Back Pain Treatments",
    "Joint Pain Injections (Knee, Shoulder, Hip)",
    "Botox for Migraine & Spasm Relief",
    "Pain Rehabilitation & Physiotherapy",
    "Cancer Pain Management Programs",
  ];

  const whyChoose = [
    {
      icon: Users,
      title: "Expert Pain Specialists",
      description:
        "Experienced Pain Medicine specialists & anesthesiologists",
    },
    {
      icon: Award,
      title: "Advanced Procedures",
      description:
        "Minimally invasive image-guided procedures",
    },
    {
      icon: Heart,
      title: "Personalized Care",
      description:
        "Personalized pain management & rehabilitation plans",
    },
    {
      icon: Shield,
      title: "Physiotherapy Support",
      description:
        "Advanced Physiotherapy & chronic pain therapy support",
    },
    {
      icon: Clock,
      title: "Multidisciplinary Approach",
      description:
        "Multidisciplinary approach with neurology, orthopaedics & oncology",
    },
  ];

  const articles = [
    {
      title:
        "Understanding Chronic Pain — Causes & Treatment Options",
      excerpt:
        "Explore the various causes of chronic pain and discover modern treatment approaches for long-term relief.",
    },
    {
      title: "How to Manage Back Pain Without Surgery",
      excerpt:
        "Learn about non-surgical interventions and lifestyle modifications that effectively manage back pain.",
    },
    {
      title: "Interventional Pain Procedures: Are They Safe?",
      excerpt:
        "Comprehensive guide to understanding the safety and effectiveness of interventional pain treatments.",
    },
    {
      title:
        "Lifestyle Habits That Help Reduce Daily Pain",
      excerpt:
        "Discover simple lifestyle changes that can significantly reduce chronic pain and improve quality of life.",
    },
  ];

  const faqs = [
    {
      question: "What conditions do pain specialists treat?",
      answer:
        "Pain Medicine doctors treat back pain, joint pain, nerve pain, headaches, cancer pain & chronic pain disorders.",
    },
    {
      question: "Are pain injections safe?",
      answer:
        "Yes, they are performed under imaging guidance for maximum safety and effectiveness.",
    },
    {
      question: "Is surgery required for chronic pain?",
      answer:
        "Not always. Many chronic pain conditions are treated successfully with minimally invasive procedures.",
    },
  ];
  const schema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "MAMS Hospitals Pain Medicine Department",
    "url": "https://mamshospitals.com/pain-medicine",
    "logo": "https://mamshospitals.com/MAMS-Logo.png",
    "image": "https://mamshospitals.com/PainMedicine.jpg",
    "description": "MAMS Hospitals provides advanced pain medicine treatment in Hyderabad including chronic pain management, spine pain treatment, nerve blocks, epidural injections, migraine treatment, and interventional pain procedures.",
    "medicalSpecialty": "Pain Management",
    "telephone": "+91-7399999981",
    "priceRange": "$$",

    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500090",
      "addressCountry": "IN"
    },

    "availableService": [
      {
        "@type": "MedicalProcedure",
        "name": "Epidural Steroid Injections"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Radiofrequency Ablation (RFA)"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Nerve Blocks & Joint Injections"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Sciatica & Spine Pain Treatment"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Cancer Pain Management"
      }
    ],

    "sameAs": [
      "https://www.facebook.com/MAMSHospitals",
      "https://www.instagram.com/mams_hospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What conditions do pain medicine specialists treat?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Pain medicine specialists treat chronic back pain, neck pain, nerve pain, joint pain, migraine, cancer pain, and spine-related disorders."
        }
      },
      {
        "@type": "Question",
        "name": "Are pain injections safe?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, all pain management injections are performed under imaging guidance using advanced safety protocols."
        }
      },
      {
        "@type": "Question",
        "name": "Can chronic pain be treated without surgery?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, many chronic pain conditions can be managed effectively with minimally invasive pain procedures and rehabilitation."
        }
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Pain Medicine Department - MAMS Hospitals",
    "url": "https://mamshospitals.com/pain-medicine",
    "description": "Best pain medicine hospital in Hyderabad offering chronic pain management, epidural injections, nerve blocks, migraine treatment, spine pain treatment, and interventional pain procedures.",
    "keywords": [
      "Pain Management Hospital Hyderabad",
      "Best Pain Specialist Hyderabad",
      "Chronic Pain Treatment Hyderabad",
      "Back Pain Treatment Hyderabad",
      "Sciatica Treatment Hyderabad",
      "Migraine Treatment Hyderabad",
      "Pain Medicine Hospital Bachupally"
    ]
  }
];

  return (
    <>
    <SEO
      title="Best Pain Management Hospital in Hyderabad | Pain Medicine Specialists | MAMS Hospitals"
      description="MAMS Hospitals offers advanced pain medicine treatment in Hyderabad including chronic pain management, epidural injections, migraine treatment, spine pain treatment, nerve blocks, and interventional pain procedures."
      keywords="pain management hospital Hyderabad,
    best pain specialist Hyderabad,
    chronic pain treatment Hyderabad,
    back pain treatment Hyderabad,
    neck pain treatment Hyderabad,
    sciatica treatment Hyderabad,
    migraine treatment Hyderabad,
    nerve block injections Hyderabad,
    epidural injection Hyderabad,
    radiofrequency ablation Hyderabad,
    spine pain treatment Hyderabad,
    pain medicine hospital Bachupally,
    MAMS Hospitals Pain Medicine"
      schema={schema}
    />
    <div className="flex flex-col min-h-screen">

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 md:py-24">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">

            <div>
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Best Pain Medicine Hospital in Hyderabad
              </div>

              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                Life is Better Without Pain — Expert Relief Begins Here
              </h1>

              <p className="text-lg text-gray-700 mb-8">
                MAMS Hospitals is recognized as one of the Best Pain Medicine Hospitals in Hyderabad, offering advanced diagnosis and treatment for acute and chronic pain conditions with evidence-based therapies and personalized care.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-teal-600 text-white p-6 rounded-2xl shadow-xl">
                <div className="text-3xl font-bold">95%</div>
                <div className="text-sm">Pain Relief Rate</div>
              </div>

              <img
                src="/PainMedicine.jpg"
                alt="Pain Medicine at MAMS Hospital"
                className="rounded-2xl shadow-2xl w-full"
              canonicalUrl="https://mamshospitals.com/departments/pain-medicine"
      />
            </div>

          </div>
        </div>
      </section>

      {/* Sub-Specialties Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container max-w-6xl mx-auto px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Sub-Specialties in Pain Medicine
          </h2>

          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
            Comprehensive pain management across multiple specializations
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((item, idx) => (
              <Card key={idx} className="group hover:shadow-xl border-gray-200 hover:border-teal-500 transition">
                <CardContent className="p-0">

                  <div className="relative h-48 overflow-hidden rounded-t-lg">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition"
                    />
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold">{item.title}</h3>
                    <p className="text-gray-600 mt-2">{item.description}</p>
                  </div>

                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* Treatments Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container max-w-6xl mx-auto px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Treatments & Procedures
          </h2>

          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
            Advanced pain relief procedures performed with precision & care
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {treatments.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle2 className="text-teal-600 h-6 w-6 mt-1" />
                <span className="text-gray-700 font-medium">{item}</span>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container max-w-6xl mx-auto px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Why Choose MAMS Hospitals for Pain Medicine
          </h2>

          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
            Trusted pain management excellence with patient-centered care
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, idx) => (
              <Card key={idx} className="border-gray-200 hover:shadow-lg transition">
                <CardContent className="p-6">

                  <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                    <item.icon className="text-teal-600 h-7 w-7" />
                  </div>

                  <h3 className="text-xl font-bold">{item.title}</h3>
                  <p className="text-gray-600 mt-2">{item.description}</p>

                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container max-w-4xl mx-auto px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Frequently Asked Questions
          </h2>

          <p className="text-gray-600 text-center mb-12">
            Get answers to common questions about pain medicine
          </p>

          <Accordion type="single" collapsible>
            {faqs.map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`}>
                <AccordionTrigger>{faq.question}</AccordionTrigger>
                <AccordionContent>{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

        </div>
      </section>

      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department </p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
    </>
  );
}
